import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Container, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import api from "../Utils/api";
import "./PublicValidationPage.css";

const statusMessage = {
  superseded: "Esta cópia foi substituída por uma versão posterior e não deve ser utilizada como versão atual.",
  revoked: "Esta versão documental foi invalidada pelo sistema.",
  integrity_error: "Não foi possível confirmar a integridade do arquivo desta versão.",
  invalid: "Não foi possível validar este documento."
};

export default function PublicValidationPage() {
  const { token } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const authenticated = Boolean(localStorage.getItem("x-access-token"));

  const validate = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(
        `/incidentReport/documents/validation/${encodeURIComponent(token)}`
      );
      if (data.valid && localStorage.getItem("x-access-token")) {
        try {
          const { data: details } = await api.get(
            `/incidentReport/documents/validation/${encodeURIComponent(token)}/details`
          );
          setResult({ ...data, ...details });
        } catch {
          setResult({ ...data, authorized: false });
        }
      } else {
        setResult(data);
      }
    } catch {
      setResult(null);
      setError("O serviço de validação está temporariamente indisponível.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    validate();
  }, [validate]);

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    setError("");
    try {
      const { data, headers } = await api.get(
        `/incidentReport/documents/validation/${encodeURIComponent(token)}/pdf`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `BO_${String(result?.documentNumber || "documento").replace(/[^a-zA-Z0-9_-]/g, "_")}_v${headers["x-document-version"] || result?.version}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (downloadError) {
      const data = downloadError.response?.data;
      let message = "A cópia integral não está disponível para esta conta.";
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text()).message || message;
        } catch {
          // Keep the generic access message for non-JSON responses.
        }
      }
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <main className="validation-page">
      <Container className="py-4 py-md-5">
        <Card className="validation-card mx-auto">
          <Card.Body className="p-4 p-md-5">
            <p className="validation-eyebrow">BO Online · 4º Batalhão de Polícia Militar</p>
            <h1>Validação de cópia digital</h1>
            <p className="validation-intro">
              A consulta confirma o registro e a integridade da versão emitida. Ela não equivale a uma assinatura digital.
            </p>

            {loading && (
              <div className="validation-loading" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Verificando o documento...
              </div>
            )}

            {!loading && error && (
              <Alert variant="warning" role="alert">
                {error}
                <div className="mt-3">
                  <Button variant="outline-dark" size="sm" onClick={validate}>
                    Tentar novamente
                  </Button>
                </div>
              </Alert>
            )}

            {!loading && result?.valid && (
              <Alert variant="success">
                <strong>Documento reconhecido; integridade da versão confirmada.</strong>
                <dl className="validation-details mb-0 mt-3">
                  <dt>Número do boletim</dt>
                  <dd>{result.documentNumber}</dd>
                  <dt>Versão documental</dt>
                  <dd>{result.version}</dd>
                  <dt>Emitida em</dt>
                  <dd>{new Date(result.issuedAt).toLocaleString("pt-BR")}</dd>
                  <dt>Status</dt>
                  <dd>Cópia válida no sistema</dd>
                </dl>
              </Alert>
            )}

            {!loading && result && !result.valid && (
              <Alert variant={result.status === "unavailable" ? "warning" : "danger"}>
                {result.status === "unavailable"
                  ? result.message
                  : statusMessage[result.status] || statusMessage.invalid}
              </Alert>
            )}

            {!loading && result?.valid && authenticated && result.authorized && (
              <Button
                variant="primary"
                onClick={download}
                disabled={downloading}
                className="w-100"
              >
                {downloading ? "Verificando acesso..." : "Baixar cópia integral"}
              </Button>
            )}

            {!loading && result?.valid && (!authenticated || !result.authorized) && (
              <Alert variant="info" className="mb-0">
                {!authenticated
                  ? "A validação pública não libera o conteúdo integral. Entre no sistema com uma conta autorizada para solicitar a cópia."
                  : "A conta atual não está autorizada a consultar o conteúdo integral deste boletim."}
                <div className="mt-3">
                  {!authenticated && (
                    <Button as={Link} to="/login" variant="outline-primary">
                      Entrar no BO Online
                    </Button>
                  )}
                </div>
              </Alert>
            )}

            <p className="validation-footnote">
              A confirmação depende da disponibilidade e da consulta atual ao sistema.
            </p>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}
